import { ApiError } from './client';

/** Die Meldungen eines einzelnen Feldes. */
export type ValidationErrorMessages = string[];

/** Ein Zwischenknoten: Teilfeld → Meldungen oder wieder ein Knoten. */
export interface ValidationErrorTree {
    [key: string]: ValidationErrorMessages | ValidationErrorTree
}

/**
 * Feld → Fehlermeldungen. Verschachtelte Attribute (`series.end`) werden als
 * `{ series: { end: [...] } }` abgebildet, indizierte FormArray-Attribute
 * (`applicationMembers[0].street`) als `{ applicationMembers: { 0: { street: [...] } } }`.
 *
 * Angular legt fuer den FormArray-Fall ein echtes Array an (`HttpErrorHandler`, customer-apps/http).
 * Hier ist es ein nach Index GESCHLUESSELTES Objekt: `errors.applicationMembers[0]` liest sich
 * identisch (JS wandelt den numerischen Zugriff in einen String-Key), aber der Typ bleibt ein
 * Baum — ein Array-Zweig im Union-Typ haette jede bestehende `Array.isArray(value)`-Verengung der
 * zehn Aufrufstellen in shared/, club-employee-app und club-offering aufgeweicht, ohne dass sich
 * zur Laufzeit dort irgendetwas aendert.
 */
export type ValidationErrors = Record<string, ValidationErrorMessages | ValidationErrorTree>;

interface ErrorDetailEntry {
    key: string
    value: string
}

/**
 * Parst die Feld-Validierungsfehler aus einer 400-Antwort des Backends.
 *
 * Die API liefert API-weit dieselbe Struktur:
 * `{ error: { details: [ [ {key:'attributeName',value:'title'}, {key:'constraintMessage',value:'…'} ], … ] } }`.
 * Spiegelt den Nicht-FormArray-Zweig des Angular `HttpErrorHandler`:
 * flache Attribute (`title`) → `{ title: [msg] }`, punktierte (`series.end`) → `{ series: { end: [msg] } }`.
 * Die `constraintMessage` ist bereits serverseitig lokalisiert (nicht durch vue-i18n schicken).
 *
 * Gibt `null` zurück, wenn der Fehler kein 400 mit auswertbaren `details` ist.
 */
export async function parseValidationErrors(error: unknown): Promise<ValidationErrors | null> {
    if (!(error instanceof ApiError) || error.response.status !== 400) {
        return null;
    }

    const body = await error.response
        .clone()
        .json()
        .catch(() => null);
    const details = body?.error?.details;
    if (!Array.isArray(details)) {
        return null;
    }

    const result: ValidationErrors = {};
    for (const detail of details) {
        if (!Array.isArray(detail)) {
            continue;
        }
        const attributeName = detail.find((e: ErrorDetailEntry) => e.key === 'attributeName')?.value;
        const message = detail.find((e: ErrorDetailEntry) => e.key === 'constraintMessage')?.value;
        if (!attributeName || !message) {
            continue;
        }

        // `applicationMembers[0].street` bzw. `…[0].sepaMandate.iban` — Angulars FormArray-Zweig
        // (`HttpErrorHandler`, customer-apps/http): der Index wird zum Array-Index, damit der
        // Aufrufer die Fehler der n-ten Person an das n-te Control reichen kann.
        const indexed = attributeName.match(/^([a-z0-9]+)\[(\d+)\]\.([a-z0-9.]+)$/i);
        if (indexed) {
            const [, arrayName, indexValue, nodeName] = indexed;
            const list = (result[arrayName] ??= {}) as ValidationErrorTree;
            const entry = (list[indexValue] ??= {}) as ValidationErrorTree;
            if (nodeName.includes('.')) {
                const [parent, child] = nodeName.split('.');
                const nested = (entry[parent] ??= {}) as ValidationErrorTree;
                nested[child] = [message];
            } else {
                entry[nodeName] = [message];
            }
        } else if (attributeName.includes('.')) {
            const [parent, child] = attributeName.split('.');
            const node = (result[parent] ??= {}) as Record<string, string[]>;
            node[child] = [message];
        } else {
            result[attributeName] = [message];
        }
    }

    return Object.keys(result).length ? result : null;
}

/**
 * Flacht den Wert eines ValidationErrors-Eintrags zu einer Meldungsliste — beliebig tief, seit der
 * FormArray-Zweig eine dritte Ebene erzeugen kann (`applicationMembers.0.sepaMandate.iban`). Ein
 * einstufiges `Object.values(...).flat()` reichte dafuer nicht: die zweite Ebene kaeme als Objekt
 * durch und stuende als `[object Object]` in der Fehlerliste.
 */
export function flattenValidationNode(msgs: ValidationErrorMessages | ValidationErrorTree): string[] {
    if (Array.isArray(msgs)) {
        return msgs;
    }
    return Object.values(msgs).flatMap(flattenValidationNode);
}

/** Flacht alle Validierungsfehler (über alle Felder) zu einer einzigen Meldungsliste. */
export function flattenValidationMessages(validation: ValidationErrors): string[] {
    return Object.values(validation).flatMap(flattenValidationNode);
}

/**
 * Filtert die Validierungsfehler auf die bekannten Formularfelder und gibt sie pro Feld geflacht
 * zurück – passend für eine inline Anzeige an `kgr-form-field` / `KgrInputGroup`.
 */
export function getFormValidationErrors<K extends string>(
    validation: ValidationErrors,
    fields: readonly K[],
): Partial<Record<K, string[]>> {
    const result: Partial<Record<K, string[]>> = {};
    for (const [field, msgs] of Object.entries(validation)) {
        if ((fields as readonly string[]).includes(field)) {
            result[field as K] = flattenValidationNode(msgs);
        }
    }
    return result;
}

/**
 * Sammelt die Validierungsfehler, die zu keinem der bekannten Formularfelder gehören, als flache
 * Liste – für eine zusätzliche Fehleranzeige (mirrors Angular additionalErrorMessages).
 */
export function getUnmappedValidationErrors(validation: ValidationErrors, fields: readonly string[]): string[] {
    const extra: string[] = [];
    for (const [field, msgs] of Object.entries(validation)) {
        if (!fields.includes(field)) {
            extra.push(...flattenValidationNode(msgs));
        }
    }
    return extra;
}
